import ls from 'localstorage-slim';
import {
  localStorageKeysConstants,
  SCASessionTypesConstants,
  SCAActionsConstants,
  PERSISTENT_SCA_ACCESS_KEY, SCAEnrollmentMethodConstants,
} from '../../constants';

const {FIVE_MINUTES_SESSION, LONG_SESSION, PER_OPERATION_SESSION} = SCASessionTypesConstants;
const {SCA_HEADER_TYPE, SCA_SESSION_KEY} = localStorageKeysConstants;

const getObjProp = (data, key, defaultValue) => data ? data[key] || defaultValue : defaultValue;

const SCA_OPERATION_LS_PREFIX = 'sca-operation';

// What the SCA check answers (403) when the session key was issued to another user
// (lib-backend-shared sca/snippets.py, step 2). The exclamation mark tells it apart from
// app-banking's plain "You are not allowed to perform this action" authorizer refusal.
const SCA_OTHER_USER_DETAIL = 'You are not allowed to perform this action!';

/**
 * Collection of helper utilities for Strong Customer Authentication (SCA) session management, token storage, and auth headers.
 * @type {Object}
 * @example
 * import { scaHelpers } from './sca.helpers';
 * const operationType = scaHelpers.getOperationType('display_card_details');
 */
export const scaHelpers = {
  /**
   * Returns the SCA session type constant for a given operation name.
   * @param {string} operationName - The SCA operation/action name to classify.
   * @returns {string} Session type constant: LONG_SESSION, FIVE_MINUTES_SESSION, or PER_OPERATION_SESSION.
   * @example
   * scaHelpers.getOperationType('balance_loading'); // LONG_SESSION
   */
  getOperationType: (operationName) => {
    if (operationName === PERSISTENT_SCA_ACCESS_KEY) return LONG_SESSION;

    // Grouped actions by session type
    const longSessionActions = new Set([
      SCAActionsConstants.OVERVIEW_CHARTS_AND_TABLES_LOADING_ACTION,
      SCAActionsConstants.BALANCE_LOADING,
      SCAActionsConstants.TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION,
      SCAActionsConstants.TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION,
    ]);

    const fiveMinutesSessionActions = new Set([
      SCAActionsConstants.ADD_FUNDS_LOADING,
      SCAActionsConstants.TRANSACTIONS_DOWNLOAD_PDF_AND_CSV_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CREATION_WITH_CARD_ACTION,
      SCAActionsConstants.SUBSCRIPTION_BULK_CREATION_WITH_CARD_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CARD_RECREATE_ACTION,
      SCAActionsConstants.CARD_CREATION_ACTION,
      SCAActionsConstants.CARD_EDITION_ACTION,
      SCAActionsConstants.BENEFICIARY_VALIDATE_ACTION,
      SCAActionsConstants.VENDOR_CREATION_ACTION,
      SCAActionsConstants.LOCK_CARD_ACTION,
    ]);

    const perOperationSessionActions = new Set([
      SCAActionsConstants.SUBSCRIPTION_CARD_CREATION_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CHANGE_CARD_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CHANGE_PAYMENT_METHOD_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CHANGE_STATUS_ACTION,
      SCAActionsConstants.DISPLAY_CARD_DETAILS_ACTION,
      SCAActionsConstants.UNLOCK_CARD_ACTION,
      SCAActionsConstants.DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION,
      SCAActionsConstants.ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION,
    ]);

    // Determine operation type efficiently
    if (longSessionActions.has(operationName)) return LONG_SESSION;
    if (fiveMinutesSessionActions.has(operationName)) return FIVE_MINUTES_SESSION;
    if (perOperationSessionActions.has(operationName)) return PER_OPERATION_SESSION;

    return PER_OPERATION_SESSION; // Default fallback
  },
  /**
   * Removes every stored SCA token from localStorage.
   *
   * Tokens are stored per session type (`sca-operation-long_session`, `-session`, `-operation`, see
   * `storeOperationToken`), not per action. This used to remove `sca-operation-<action name>` keys,
   * which are never written, so logout left the previous user's tokens behind (H1MVP-5214).
   * @returns {void}
   * @example
   * scaHelpers.clearStoredData();
   */
  clearStoredData: () => {
    Object.values(SCASessionTypesConstants).forEach(key => ls.remove(`${SCA_OPERATION_LS_PREFIX}-${key}`))
  },
  /**
   * Persists an SCA operation token (header type and session key) to localStorage with a TTL based on the operation type.
   * @param {Object} params - Parameters object.
   * @param {string} params.headerType - The SCA header type value to store.
   * @param {string} params.operationName - The SCA operation/action name used as the storage key and to determine TTL.
   * @param {string} params.sessionKey - The SCA session key to store.
   * @returns {void}
   * @example
   * scaHelpers.storeOperationToken({ headerType: 'otp', operationName: 'lock_card', sessionKey: 'abc123' });
   */
  storeOperationToken: ({headerType, operationName, sessionKey}) => {
    const ttls = {
      [PER_OPERATION_SESSION]: 2,
      // [FIVE_MINUTES_SESSION]: 300, // 5 minutes
      [LONG_SESSION]: 7776000 // 90 days
    };
    const operationType = scaHelpers.getOperationType(operationName);
    const ttl = ttls[operationType] || null;

    ls.set(
      `${SCA_OPERATION_LS_PREFIX}-${operationType}`,
      {
        [SCA_HEADER_TYPE]: headerType,
        [SCA_SESSION_KEY]: sessionKey
      },
      { encrypt: true, ttl }
    );
  },
  /**
   * Retrieves and decrypts the stored SCA token for a given operation from localStorage.
   * @param {string} operationType - The SCA operation/action name used as the storage key.
   * @returns {Object|null} Decrypted token object with SCA_HEADER_TYPE and SCA_SESSION_KEY, or null if not found or expired.
   * @example
   * scaHelpers.getOperationToken('lock_card');
   */
  getOperationToken: (operationType) => ls.get(`${SCA_OPERATION_LS_PREFIX}-${operationType}`, { decrypt: true }),
  /**
   * Builds the SCA request headers object for a given operation, falling back to defaultHeaders if provided.
   * @param {string} operationName - The SCA operation/action name used to look up the stored token.
   * @param {Object|null} defaultHeaders - If provided, returned as-is instead of building from stored token.
   * @returns {Object} Headers object with 'sca-header-type' and optionally 'sca-session-key'.
   * @example
   * scaHelpers.getAuthHeaders('display_card_details', null);
   */
  getAuthHeaders: (operationName, defaultHeaders) => {
    if (defaultHeaders) return defaultHeaders;
    const operationType = scaHelpers.getOperationType(operationName);
    const token = scaHelpers.getOperationToken(operationType) || operationName || '';
    let headers = {
      'sca-header-type': getObjProp(token, SCA_HEADER_TYPE) || SCAEnrollmentMethodConstants.WITHLESS_MOBILE
    }
    if (token) {
      headers = {
        ...headers,
        'sca-session-key': getObjProp(token, SCA_SESSION_KEY, '')
      }
    }
    return headers;
  },
  /**
   * Handles an SCA error response by invoking the appropriate callback based on the HTTP status code and detail message.
   * @param {Object} params - Parameters object.
   * @param {Object} params.response - Axios error response object with a nested response property.
   * @param {Function} [params.scaCallback] - Called with { isEnrolled: boolean, open: boolean } for SCA-required statuses (406, 408).
   * @param {Function} [params.errorCallback] - Called with the raw response for non-SCA errors.
   * @returns {void}
   * @example
   * scaHelpers.SCAResponseCallback({ response: axiosError, scaCallback: openSCAModal, errorCallback: handleError });
   */
  SCAResponseCallback: ({response, scaCallback, errorCallback}) => {
    const statusCode = getObjProp(response.response, 'status') || 408;
    let detail, message;
    try {
      detail = getObjProp(response.response.data, 'detail');
      // app-backend (flask-restx) sends the reason as `message`, the FastAPI services as `detail`
      message = getObjProp(response.response.data, 'message');
    } catch {}
    if (statusCode === 403 && (detail || message) === SCA_OTHER_USER_DETAIL) {
      // The stored token was issued to someone else: drop it and ask this user to authenticate.
      scaHelpers.clearStoredData();
      scaCallback && scaCallback({isEnrolled: true, open: true});
    } else if ((statusCode === 406 && detail === 'Invalid SMS code. Please try again.') || statusCode === 408) {
      scaCallback && scaCallback({isEnrolled: true, open: true});
    } else if (statusCode === 406) {
      scaCallback && scaCallback({isEnrolled: false, open: true});
    } else {
      errorCallback && errorCallback(response);
    }
  },
  /**
   * Returns props for the SCA authentication modal, including an optional translated title based on the action.
   * @param {Object} params - Parameters object.
   * @param {string} params.action - The SCA action constant used to determine the modal title.
   * @param {Function} params.t - i18next translation function.
   * @returns {{ title: string|undefined }} Modal props object with an optional title string.
   * @example
   * scaHelpers.getAuthModalProps({ action: SCAActionsConstants.TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION, t });
   */
  getAuthModalProps: ({action, t}) => {
    const transactionTitle = `${t('main:transactions')} ${t('main:loading')}`
    const title = {
      [SCAActionsConstants.TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION]: transactionTitle,
      [SCAActionsConstants.TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION]: transactionTitle,
    }[action] || undefined;
    return { title };
  }
}
